import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { cn } from '@/lib/utils'

/**
 * The shared server-403 state (PX-W6.2, AC-7) — the blocked page for a call
 * the SERVER refused, in the same visual grammar as `RoleBlockedPage`: what
 * was requested, that access was refused, and a way back.
 *
 * Unlike `RoleBlockedPage` this renders INSIDE a surface that already owns
 * the shell, because the refusal arrived as data (the mapped `ApiError`
 * envelope), not as a route decision. It states a fact; it never re-checks,
 * re-derives or names permissions (INV-2) — the 403 came from the server and
 * the server's word is rendered verbatim.
 *
 * PX-W6.6 stamps `data-triad="forbidden"` on the root: a refusal is a FOURTH
 * state beside the triad, never the error state wearing different words.
 */
export function ForbiddenPanel({
  requested,
  className
}: {
  /** What the surface asked the server for (a path or a plain phrase). */
  requested?: string
  className?: string
}) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()

  return (
    <div
      role="alert"
      data-triad="forbidden"
      data-testid="forbidden-panel"
      className={cn('w-full max-w-3xl rounded-lg border border-border bg-card px-5 py-5', className)}
    >
      <div className="flex items-center gap-2">
        <StatusPill tone="blocked">{t('forbidden.title')}</StatusPill>
      </div>

      {requested !== undefined && requested !== '' && (
        <div className="mt-3">
          <SectionLabel>{t('forbidden.requestedLabel')}</SectionLabel>
          <p className="mt-1 font-mono text-sm text-foreground">{requested}</p>
        </div>
      )}

      <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
        {t('forbidden.refused')}
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted"
        >
          {t('forbidden.back')}
        </button>
        <Link
          to="/"
          className="inline-flex h-8 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t('forbidden.home')}
        </Link>
      </div>
    </div>
  )
}
